<template>
  <div class="client-list-container">
    <b-card no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
              cols="12"
              md="3"
          >
            <label>Afficher</label>
            <v-select
                v-model="perPage"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                class="per-page-selector d-inline-block mx-50"
            />
            <label>Clients</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="3">
            <b-form-group>
              <b-input-group>
                <b-form-input
                    v-model="searchQuery"
                    placeholder="Nom, email..."
                />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3">
            <b-form-group>
              <v-select
                  v-model="selectedStatus"
                  :clearable="false"
                  :options="statusOptions"
                  :reduce="option => option.value"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3">
            <b-button
                :to="{ name: 'apps-client-add' }"
                class="mr-1"
                variant="primary"
            >
              <feather-icon class="mr-50" icon="PlusIcon"/>
              <span>Ajouter un client</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-if="$can('merge_customer', 'Clients')" class="mx-2 mb-2">
        <b-button v-if="!isMergeMode" variant="outline-primary" @click="startMergeMode">
          Fusionner des clients
        </b-button>
        <template v-else>
          <b-button variant="outline-primary" :disabled="selectedClients.length < 2" @click="showMergeModal = true">
            Fusionner ({{ selectedClients.length }})
          </b-button>
          <b-button v-if="selectedClients.length" variant="link" @click="selectedClients = []">Effacer la sélection</b-button>
          <b-button variant="outline-secondary" class="ml-1" @click="cancelMergeMode">Annuler la fusion</b-button>
          <p class="text-muted mt-1 mb-0">Sélectionnez au moins deux clients. La sélection est conservée entre les pages et les recherches.</p>
        </template>
      </div>
      <client-merge-modal
          v-if="$can('merge_customer', 'Clients')"
          :visible="showMergeModal"
          :clients="selectedClients"
          @close="showMergeModal = false"
          @merged="onClientsMerged"
      />
      <div class="client-list-table">
        <b-table
            ref="refClientsListTable"
            :busy="loading"
            :fields="mergeTableColumns"
            :items="fetchClients"
            empty-text="Aucun client trouvé"
            hover
            responsive
            show-empty
            striped
        >
          <template #cell(mergeSelection)="data">
            <b-form-checkbox
                :checked="selectedClients.some(client => client.uid === data.item.uid)"
                :aria-label="`Sélectionner ${data.item.name} (#${data.item.uid})`"
                @change="checked => selectClient(data.item, checked)"
            />
          </template>
          <template #cell(name)="data">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                    :text="avatarText(data.item.name[0])"
                    :to="{ name: 'apps-client-view', params: { id: data.item.uid } }"
                    size="32"
                />
              </template>
              <b-link
                  :id="`tooltip-client-name-${data.item.uid}`"
                  :to="{ name: 'apps-client-view', params: { id: data.item.uid } }"
                  class="font-weight-bold d-block"
              >
                {{
                  truncateName(data.item.name)
                }}
              </b-link>
              <b-tooltip :target="`tooltip-client-name-${data.item.uid}`" triggers="hover" position="topright">
                {{  data.item.name }}
              </b-tooltip>

              <small class="text-muted text-truncate">{{ getDetailsData(data.item.details, 'email') }} /
                {{ getDetailsData(data.item.details, 'phone') }}</small>
            </b-media>
          </template>
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle" variant="primary"></b-spinner>
              <span class="ml-1">Chargement...</span>
            </div>
          </template>

          <!-- Column: Status -->
          <template #cell(status)="data">
            <b-form-checkbox
                :checked="data.item.status"
                class="custom-control-success text-capitalize"
                name="check-button"
                switch
                @change="(checked) => toggleClientStatus(checked, data.item.uid)"
            >
            <span class="switch-icon-left">
              <feather-icon icon="CheckIcon"/>
            </span>
              <span class="switch-icon-right text-capitalize">
              <feather-icon icon="XIcon"/>
            </span>
            </b-form-checkbox>
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <feather-icon
                  :id="`client-row-${data.item.uid}-view-icon`"
                  class="cursor-pointer text-warning"
                  icon="EyeIcon"
                  size="16"
                  @click="
                $router.push({
                  name: 'apps-client-view',
                  params: { id: data.item.uid }
                })
              "
              />
              <b-tooltip
                  :target="`client-row-${data.item.uid}-view-icon`"
                  class="cursor-pointer"
                  title="Détails"
              />

              <feather-icon
                  :id="`client-row-${data.item.uid}-edit-icon`"
                  class="mx-1 text-danger"
                  icon="EditIcon"
                  size="16"
                  @click="
                $router.push({
                  name: 'apps-client-edit',
                  params: { id: data.item.uid }
                })
              "
              />
              <b-tooltip
                  :target="`client-row-${data.item.uid}-edit-icon`"
                  title="Modifier"
              />
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="mx-2 mb-2">
          <b-row>
            <b-col class="d-flex align-items-center justify-content-center justify-content-sm-start mb-2 mb-sm-0"
                   cols="12"
                   sm="6">
              <span class="text-muted">Affichage de {{ paginationInfo }} clients</span>
            </b-col>
            <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
              <b-pagination
                  v-model="currentPage"
                  :per-page="perPage"
                  :total-rows="dataMeta.total"
                  class="mb-0"
              />
            </b-col>
          </b-row>
        </div>

        <!-- Delete Confirmation Modal -->
        <b-modal
            v-model="showDeleteModal"
            cancel-title="Annuler"
            ok-title="Supprimer"
            ok-variant="danger"
            title="Confirmer la suppression"
            @ok="handleDeleteClient"
        >
          <p>Êtes-vous sûr de vouloir supprimer le client <strong>{{ clientToDelete.name }}</strong> ?</p>
          <p class="mb-0 text-danger">Cette action est irréversible.</p>
        </b-modal>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BLink,
  BMedia, BModal,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  BTooltip
} from "bootstrap-vue";
import {avatarText} from "@core/utils/filter";
import vSelect from "vue-select"
import useClientsList from "@/views/apps/client/clients-list/useClientsList";
import {ref} from "@vue/composition-api";
import ClientMergeModal from "./ClientMergeModal.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: 'ClientsList',
  components: {
    ClientMergeModal,
    BModal,
    BFormGroup, BInputGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BCard,
    BAvatar,
    BFormCheckbox,
    BLink,
    BMedia,
    BPagination,
    BSpinner,
    BTable,
    BTooltip,
    vSelect,
  },

  data() {
    return {isMergeMode: false, selectedClients: [], showMergeModal: false};
  },
  computed: {
    mergeTableColumns() {
      return this.isMergeMode && this.$can('merge_customer', 'Clients')
        ? [{key: 'mergeSelection', label: 'Sélection'}, ...this.tableColumns]
        : this.tableColumns;
    },
  },
  methods: {
    startMergeMode() {
      this.isMergeMode = true;
    },
    cancelMergeMode() {
      this.showMergeModal = false;
      this.selectedClients = [];
      this.isMergeMode = false;
    },
    selectClient(client, checked) {
      this.selectedClients = this.selectedClients.filter(item => item.uid !== client.uid);
      if (checked) this.selectedClients.push(client);
    },
    onClientsMerged() {
      this.cancelMergeMode();
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Les clients ont été fusionnés avec succès.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      });
      if (this.currentPage !== 1) this.currentPage = 1;
      else this.refClientsListTable.refresh();
    },
  },
  setup() {
    const clientToDelete = ref({
      id: null,
      name: '',
    });

    const {
      searchQuery,
      perPage,
      currentPage,
      perPageOptions,
      sortBy,
      isSortDirDesc,
      paginationInfo,
      statusOptions,
      typeOptions,
      dataMeta,
      selectedType,
      selectedStatus,
      refClientsListTable,
      tableColumns,
      clients,
      loading,
      fetchClients,
      getDetailsData,
      showDeleteModal,
      toggleClientStatus,
      handleDeleteClient,
    } = useClientsList()

    const truncateName = (name) => {
      return name.length > 30 ? name.substring(0, 30) + '...' : name
    }

    return {
      avatarText,
      searchQuery,
      perPage,
      currentPage,
      perPageOptions,
      sortBy,
      isSortDirDesc,
      paginationInfo,
      statusOptions,
      typeOptions,
      dataMeta,
      refClientsListTable,
      tableColumns,
      selectedType,
      selectedStatus,
      clients,
      loading,
      showDeleteModal,
      fetchClients,
      getDetailsData,
      toggleClientStatus,
      handleDeleteClient,
      clientToDelete,
      truncateName,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select';
</style>